import { ErrorBanner } from '@/components';
import type { Country } from '@/features/country/country';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { CompanyFormData, Level } from '../models';
import { AdminSection } from './AdminSection';
import { CompanyAddressSection } from './CompanyAddressSection';
import { CompanyDataSection } from './CompanyDataSection';
import { FieldSalesRepresentativeSection } from './FieldSalesRepresentativeSection';
import { cn } from '@/lib';
import { scrollStyleClass } from '@/utils/scrollClassHelper';

interface StammdatenFormProps {
  initialData?: CompanyFormData;
  countries: Country[];
  onSave: (data: CompanyFormData) => void;
  isSaving: boolean;
  error?: string | null;
  showAdminSection?: boolean;
  companyId?: string;
  parentCompanyId?: string;
  parentCompanyName?: string;
  onParentCompanyChange?: (targetCompanyId: string) => void;
  canEditCustomerId?: boolean;
}

const emptyFormData: CompanyFormData = {
  companyName: '',
  street: '',
  houseNumber: '',
  addressLine2: '',
  postalCode: '',
  city: '',
  country: '',
  phoneNumber: '',
  email: '',
  taxNumber: '',
  uidNumber: '',
  customerId: '',
  companyId: undefined,
  level: undefined,
};

export function StammdatenForm({
  initialData,
  countries,
  onSave,
  isSaving,
  error,
  showAdminSection = false,
  companyId,
  parentCompanyId,
  parentCompanyName,
  onParentCompanyChange,
  canEditCustomerId = false,
}: StammdatenFormProps) {
  const { t } = useTranslation();
  const [hasChanges, setHasChanges] = useState(false);
  const [formData, setFormData] = useState<CompanyFormData>(
    initialData ?? emptyFormData
  );

  useEffect(() => {
    if (initialData) {
      setFormData(initialData);
      setHasChanges(false);
    }
  }, [initialData]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setHasChanges(true);
  };

  const handleLevelChange = (level: Level) => {
    setFormData((prev) => ({ ...prev, level }));
    setHasChanges(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="relative flex h-full min-h-0 w-full flex-col items-center">
      <form
        onSubmit={handleSubmit}
        className="bg-surface flex h-full min-h-0 w-full max-w-[600px] flex-col"
      >
        <ErrorBanner hasError={!!error}>{error}</ErrorBanner>

        {/* Scrollable Content */}
        <div className={cn("flex flex-1 min-h-0 flex-col gap-10 overflow-auto", scrollStyleClass)}>
          <CompanyDataSection
            formData={formData}
            countries={countries}
            onChange={handleChange}
            showCustomerId={true}
            isCustomerIdReadOnly={!canEditCustomerId}
          />

          <CompanyAddressSection formData={formData} onChange={handleChange} />

          <FieldSalesRepresentativeSection companyId={companyId} />

          {showAdminSection && companyId && (
            <AdminSection
              formData={formData}
              companyId={companyId}
              parentCompanyId={parentCompanyId}
              parentCompanyName={parentCompanyName}
              onLevelChange={handleLevelChange}
              onParentCompanyChange={onParentCompanyChange}
            />
          )}
        </div>

        {/* Fixed Save Button */}
        <div className="flex justify-end bg-surface p-4">
          <button
            type="submit"
            disabled={isSaving || !hasChanges}
            className="bg-primary text-onprimary hover:bg-primary-darker focus:ring-primary inline-flex items-center gap-x-2 rounded-lg border border-transparent px-4 py-2 text-sm font-medium focus:ring-2 focus:outline-none disabled:pointer-events-none disabled:opacity-50"
          >
            {isSaving ? (
              <>
                <svg
                  className="size-4 animate-spin"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                {t('companyAdmin.form.saving')}
              </>
            ) : (
              t('companyAdmin.form.save')
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
